// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, Home, Download, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const VideoPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      // Find the order by stripe session ID and update status
      const { data: order, error } = await supabase
        .from('video_orders')
        .select(`
          *,
          vendor:video_vendor_profiles!vendor_id(business_name),
          event:events!event_id(name),
          package:video_packages!package_id(package_type, price_cents)
        `)
        .eq('stripe_session_id', sessionId)
        .single();

      if (error) {
        console.error('Error finding order:', error);
        toast.error('Could not verify payment');
        return;
      }

      // Update order status to paid
      const { error: updateError } = await supabase
        .from('video_orders')
        .update({ status: 'paid' })
        .eq('id', order.id);

      if (updateError) {
        console.error('Error updating order status:', updateError);
      }

      setOrderDetails(order);
      toast.success('Payment successful! Your video order has been confirmed.');
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Error verifying payment');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const getDashboardPath = () => {
    if (!profile) return '/customer-dashboard'; // Default for non-authenticated users
    
    switch (profile.role) {
      case 'parent':
        return '/parent-dashboard';
      case 'athlete':
        return '/athlete-dashboard';
      case 'admin':
        return '/admin-dashboard';
      case 'club_coach':
        return '/club-coach-dashboard';
      case 'college_coach':
        return '/college-coach-dashboard';
      default:
        return '/customer-dashboard';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Verifying payment...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-red-600">
              <AlertCircle className="h-6 w-6" />
              Payment Verification Failed
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p>We couldn't verify your payment. Please contact support.</p>
            <Button onClick={() => navigate('/')}>
              <Home className="mr-2 h-4 w-4" />
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2 text-green-600">
            <CheckCircle className="h-6 w-6" />
            Payment Successful!
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center space-y-2">
            <p className="text-muted-foreground">
              Your video order has been confirmed and the vendor has been notified.
            </p>
          </div>

          <div className="space-y-3 p-4 bg-muted rounded-lg">
            <h3 className="font-semibold">Order Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Order ID:</span>
                <span className="font-mono">{orderDetails.id.slice(0, 8)}...</span>
              </div>
              <div className="flex justify-between">
                <span>Event:</span>
                <span>{orderDetails.event?.name}</span>
              </div>
              <div className="flex justify-between">
                <span>Vendor:</span>
                <span>{orderDetails.vendor?.business_name}</span>
              </div>
              <div className="flex justify-between">
                <span>Package:</span>
                <span className="capitalize">{orderDetails.package?.package_type}</span>
              </div>
              <div className="flex justify-between">
                <span>Player:</span>
                <span>{orderDetails.player_name}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Amount Paid:</span>
                <span>{formatPrice(orderDetails.total_amount_cents)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You'll receive email updates about your video production progress. 
              The completed video will be available for download once ready.
            </p>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="flex-1"
              >
                <Home className="mr-2 h-4 w-4" />
                Home
              </Button>
              
              {profile && (
                <Button 
                  onClick={() => navigate(getDashboardPath())}
                  className="flex-1"
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  My Dashboard
                </Button>
              )}
              
              <Button 
                variant="secondary"
                onClick={() => navigate('/video-downloads')}
                className="flex-1"
              >
                <Download className="mr-2 h-4 w-4" />
                My Videos
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VideoPaymentSuccess;